import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import content from './data/site-content.json';

const initialQuoteForm = {
  nombre: '',
  telefono: '',
  email: '',
  destino: '',
  fecha: '',
  viajeros: '',
  presupuesto: '',
  mensaje: ''
};

function buildWhatsAppMessage(values, title = 'Cotización de viaje') {
  const lines = [
    `Hola ${content.brand.name}, quiero cotizar un viaje.`,
    `Nombre: ${values.nombre || 'No indicado'}`,
    `Teléfono: ${values.telefono || 'No indicado'}`,
    values.email ? `Email: ${values.email}` : null,
    `Destino/interés: ${values.destino || 'Aún no definido'}`,
    values.fecha ? `Fecha aproximada: ${values.fecha}` : null,
    values.viajeros ? `Viajeros: ${values.viajeros}` : null,
    values.presupuesto ? `Presupuesto: ${values.presupuesto}` : null,
    values.mensaje ? `Mensaje: ${values.mensaje}` : null
  ];

  return [title, ...lines.filter(Boolean)].join('\n');
}

function openWhatsApp(message) {
  const url = `https://wa.me/${content.contact.whatsapp}?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function getDestination(slug) {
  return content.destinations.find((item) => item.slug === slug);
}

function formatDestinationLabel(destination) {
  if (!destination) return 'Viaje personalizado';
  return `${destination.destination} - ${destination.title}`;
}

function Icon({ name, className = 'h-5 w-5' }) {
  const icons = {
    map: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 3v15M15 6v15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    shield: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3l7 3v5c0 4.55-2.86 8.27-7 10-4.14-1.73-7-5.45-7-10V6l7-3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    chat: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 5.5A3.5 3.5 0 017.5 2h9A3.5 3.5 0 0120 5.5v6A3.5 3.5 0 0116.5 15H11l-5 4v-4.5A3.5 3.5 0 014 11.5v-6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M8 8h8M8 11h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    star: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2.8l2.9 5.88 6.48.94-4.69 4.57 1.11 6.46L12 17.56 6.2 20.65l1.11-6.46-4.69-4.57 6.48-.94L12 2.8z" />
      </svg>
    ),
    calendar: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 3v3M17 3v3M4 9h16M6 5h12a2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    users: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M16 19c0-2.21-1.79-4-4-4s-4 1.79-4 4M12 12a4 4 0 100-8 4 4 0 000 8zM20 19c0-1.72-1.07-3.19-2.6-3.72M17 6.5a3 3 0 010 5.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    dollar: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3v18M17 7.5c-.8-1.2-2.2-2-4.2-2-2.4 0-4 1.1-4 2.8 0 4.2 8.4 1.8 8.4 6.4 0 1.9-1.8 3.3-4.7 3.3-2.2 0-4-.9-5-2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    phone: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6.6 10.8c1.2 2.36 3.14 4.3 5.5 5.5l1.85-1.85c.3-.3.75-.4 1.15-.27 1.26.42 2.16.65 2.7.68.62.03 1.14.55 1.17 1.17l.03 2.1c.02.87-.69 1.58-1.56 1.58C9.44 19.71 4.29 14.56 4.29 6.56c0-.87.71-1.58 1.58-1.56l2.1.03c.62.03 1.14.55 1.17 1.17.03.54.26 1.44.68 2.7.13.4.03.85-.27 1.15l-1.95 1.75z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    mail: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 6h16v12H4V6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    mapPin: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 21s7-4.35 7-10a7 7 0 10-14 0c0 5.65 7 10 7 10z" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 13.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    ),
    whatsapp: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.47 12.26c-.1-.16-.37-.28-.77-.48-.4-.2-2.36-1.17-2.73-1.3-.37-.14-.64-.2-.91.2-.27.4-1.05 1.3-1.29 1.57-.24.27-.48.3-.88.1-.4-.2-1.68-.62-3.2-1.97-1.18-1.06-1.98-2.36-2.22-2.76-.24-.4-.03-.62.18-.83.18-.18.4-.47.6-.7.2-.24.27-.4.4-.67.14-.27.07-.5-.03-.7-.1-.2-.91-2.19-1.25-3-.33-.79-.67-.68-.91-.69h-.78c-.27 0-.7.1-1.07.5-.37.4-1.41 1.38-1.41 3.37 0 1.99 1.45 3.92 1.65 4.19.2.27 2.85 4.35 6.91 6.1 3.28 1.42 3.95 1.14 4.66 1.07.71-.07 2.36-.96 2.7-1.9.34-.93.34-1.73.24-1.9z" />
      </svg>
    ),
    arrowRight: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    menu: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    x: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    check: (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  };

  return icons[name] || null;
}

function TravelImage({ src, alt, className = '', fallbackLabel = 'Imagen de viaje' }) {
  const [failed, setFailed] = useState(false);

  return (
    <div className={`relative overflow-hidden bg-gradient-to-br from-brand/20 via-sky-100 to-coral-100 ${className}`}>
      {failed ? (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand/20 via-sky-100 to-coral-100 p-6 text-center text-sm font-semibold text-brand-dark">
          {fallbackLabel}
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover transition duration-700 hover:scale-105"
        />
      )}
    </div>
  );
}

function SectionHeading({ eyebrow, title, subtitle, align = 'center' }) {
  return (
    <div className={`mx-auto max-w-3xl ${align === 'left' ? 'text-left' : 'text-center'}`}>
      {eyebrow && <p className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-coral">{eyebrow}</p>}
      <h2 className="text-3xl font-extrabold tracking-tight text-ink md:text-5xl">{title}</h2>
      {subtitle && <p className="mt-5 text-base leading-7 text-slate-600 md:text-lg">{subtitle}</p>}
    </div>
  );
}

function Button({ children, variant = 'primary', className = '', as = 'button', href, onClick, type = 'button', ...props }) {
  const base = 'inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-extrabold transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/20';
  const variants = {
    primary: 'bg-coral text-white shadow-lg shadow-coral/20 hover:bg-coral/90',
    secondary: 'border border-brand/20 bg-white text-brand hover:bg-brand/10',
    ghost: 'text-brand hover:bg-brand/10',
    dark: 'bg-brand text-white shadow-lg shadow-brand/20 hover:bg-brand-dark'
  };
  const Component = as;
  const common = { className: `${base} ${variants[variant]} ${className}`, onClick, type };
  return href ? <Component href={href} {...common} {...props} /> : <Component {...common} {...props}>{children}</Component>;
}

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-white/60 bg-white/78 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex min-w-0 items-center gap-3" aria-label="Ir al inicio">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand text-lg font-black text-white shadow-lg shadow-brand/20">
            {content.brand.logoMark}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-base font-black tracking-tight text-ink">{content.brand.name}</span>
            <span className="block truncate text-xs font-bold uppercase tracking-[0.18em] text-coral">{content.brand.tagline}</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Navegación principal">
          {content.nav.map((item) => (
            <Link key={item.to} to={item.to} className="rounded-full px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-brand/10 hover:text-brand">
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button as={Link} to="/cotizar" variant="primary">
            Cotizar viaje <Icon name="arrowRight" />
          </Button>
        </div>

        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-800 lg:hidden"
          aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
          onClick={() => setOpen((value) => !value)}
        >
          <Icon name={open ? 'x' : 'menu'} />
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-100 bg-white/95 px-4 py-3 shadow-xl lg:hidden">
          <nav className="mx-auto grid max-w-7xl gap-2" aria-label="Navegación móvil">
            {content.nav.map((item) => (
              <Link key={item.to} to={item.to} className="rounded-2xl px-4 py-3 text-sm font-extrabold text-slate-800 hover:bg-brand/10 hover:text-brand">
                {item.label}
              </Link>
            ))}
            <Button as={Link} to="/cotizar" variant="primary" className="mt-2 w-full">
              Cotizar viaje
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}

function Hero() {
  const navigate = useNavigate();
  const [quickSearch, setQuickSearch] = useState('');

  function submitSearch(event) {
    event.preventDefault();
    navigate(`/destinos${quickSearch ? `?search=${encodeURIComponent(quickSearch)}` : ''}`);
  }

  return (
    <section className="relative overflow-hidden px-4 pb-16 pt-10 sm:px-6 lg:px-8 lg:pb-24 lg:pt-16">
      <div className="absolute inset-x-0 top-0 -z-10 h-80 bg-gradient-to-b from-brand/10 to-transparent" />
      <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[1.02fr_0.98fr]">
        <div className="order-2 lg:order-1">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand/15 bg-white/75 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-brand shadow-sm">
            <span className="h-2 w-2 rounded-full bg-coral" />
            {content.hero.eyebrow}
          </div>
          <h1 className="max-w-4xl text-4xl font-black tracking-tight text-ink sm:text-5xl lg:text-7xl">
            {content.hero.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-8 text-slate-600 sm:text-lg md:text-xl">
            {content.hero.subtitle}
          </p>

          <form onSubmit={submitSearch} className="mt-8 max-w-2xl">
            <label htmlFor="quick-search" className="sr-only">
              Buscar destino o tipo de viaje
            </label>
            <div className="flex flex-col gap-3 rounded-[2rem] border border-white/70 bg-white p-2 shadow-travel sm:flex-row">
              <div className="flex flex-1 items-center gap-3 px-3">
                <Icon name="map" className="h-5 w-5 text-brand" />
                <input
                  id="quick-search"
                  value={quickSearch}
                  onChange={(event) => setQuickSearch(event.target.value)}
                  placeholder={content.hero.searchPlaceholder}
                  className="min-h-12 w-full bg-transparent text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400"
                />
              </div>
              <Button type="submit" variant="dark" className="sm:w-auto">
                Buscar ideas
              </Button>
            </div>
          </form>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button as={Link} to="/cotizar" variant="primary">
              {content.hero.primaryCta}
            </Button>
            <Button as={Link} to="/destinos" variant="secondary">
              {content.hero.secondaryCta}
            </Button>
          </div>

          <div className="mt-8 grid max-w-xl grid-cols-3 gap-3 rounded-[2rem] border border-white/70 bg-white/70 p-3 shadow-sm">
            {[
              ['100%', 'Personalizado'],
              ['24h', 'Respuesta rápida'],
              ['CR', 'Operadores locales']
            ].map(([value, label]) => (
              <div key={label} className="rounded-3xl bg-white px-4 py-4 text-center">
                <div className="text-lg font-black text-brand">{value}</div>
                <div className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <div className="relative">
            <div className="absolute -inset-4 -z-10 rounded-[2.5rem] bg-gradient-to-tr from-brand/25 via-sky-200/35 to-coral-200/35 blur-2xl" />
            <TravelImage
              src={content.hero.image}
              alt={content.hero.imageAlt}
              className="aspect-[4/5] rounded-[2.5rem] shadow-travel sm:aspect-[16/12] lg:aspect-[4/5]"
            />
            <div className="absolute bottom-5 left-5 right-5 rounded-[1.5rem] border border-white/50 bg-white/82 p-4 shadow-xl backdrop-blur">
              <div className="flex items-center gap-3">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand text-white">
                  <Icon name="whatsapp" className="h-6 w-6" />
                </span>
                <div>
                  <p className="text-sm font-black text-ink">Cotización por WhatsApp</p>
                  <p className="text-xs font-semibold text-slate-600">Te respondemos el mismo día</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Benefits() {
  return (
    <section className="px-4 py-14 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-5 md:grid-cols-3">
          {content.benefits.map((benefit, index) => (
            <article key={benefit.title} className="group rounded-[2rem] border border-white/70 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-travel">
              <div className="mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-brand/10 text-brand transition group-hover:bg-brand group-hover:text-white">
                <Icon name={benefit.icon} className="h-7 w-7" />
              </div>
              <p className="text-xs font-black uppercase tracking-[0.16em] text-coral">Beneficio {index + 1}</p>
              <h3 className="mt-3 text-xl font-black text-ink">{benefit.title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">{benefit.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function DestinationCard({ destination, compact = false }) {
  return (
    <article className="group overflow-hidden rounded-[2rem] border border-white/70 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-travel">
      <Link to={`/destinos/${destination.slug}`} className="block">
        <TravelImage
          src={destination.image}
          alt={destination.imageAlt}
          className={compact ? 'aspect-[4/3] sm:aspect-auto sm:min-h-[220px]' : 'aspect-[4/3]'}
        />
      </Link>
      <div className="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-brand/10 px-3 py-1 text-xs font-black text-brand">{destination.category || destination.region}</span>
          <span className="rounded-full bg-coral-soft px-3 py-1 text-xs font-black text-coral">{destination.duration}</span>
        </div>
        <h3 className="mt-4 text-xl font-black text-ink">
          <Link to={`/destinos/${destination.slug}`} className="hover:text-brand">
            {destination.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">{destination.description}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {destination.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Desde</p>
            <p className="text-lg font-black text-brand">{destination.price}</p>
          </div>
          <Button as={Link} to={`/destinos/${destination.slug}`} variant="secondary" className="px-4 py-2 text-xs">
            Ver detalle
          </Button>
        </div>
      </div>
    </article>
  );
}

function FeaturedDestinations() {
  return (
    <section className="px-4 py-14 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          eyebrow="Destinos favoritos"
          title="Itinerarios listos para convertir en tu próximo viaje"
          subtitle="Estos paquetes son una base. Los ajustamos según tus fechas, presupuesto y estilo."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {content.featuredDestinations.map((destination) => (
            <DestinationCard key={destination.slug} destination={destination} compact />
          ))}
        </div>
        <div className="mt-8 text-center">
          <Button as={Link} to="/destinos" variant="dark">
            Ver todos los destinos
          </Button>
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="px-4 py-14 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
        <div className="rounded-[2.5rem] bg-brand p-8 text-white shadow-travel">
          <p className="text-sm font-black uppercase tracking-[0.18em] text-sky-100">Cómo trabajamos</p>
          <h2 className="mt-4 text-4xl font-black tracking-tight">De la idea al itinerario, sin vueltas.</h2>
          <p className="mt-5 max-w-xl text-base leading-7 text-sky-50">
            La página está pensada para que el usuario entienda rápido el servicio y llegue a cotizar por WhatsApp con toda la información necesaria.
          </p>
        </div>
        <div className="grid gap-4">
          {content.process.map((item) => (
            <article key={item.step} className="flex gap-5 rounded-[2rem] border border-white/70 bg-white p-6 shadow-sm">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-coral text-lg font-black text-white">{item.step}</div>
              <div>
                <h3 className="text-xl font-black text-ink">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{item.description}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="px-4 py-14 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          eyebrow="Clientes felices"
          title="Viajes que se sienten organizados y humanos"
          subtitle="Testimonios de ejemplo para la POC, listos para reemplazar por reseñas reales."
        />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {content.testimonials.map((item) => (
            <article key={item.name} className="rounded-[2rem] border border-white/70 bg-white p-6 shadow-sm">
              <div className="flex gap-1 text-coral" aria-label={`${item.rating} de 5 estrellas`}>
                {Array.from({ length: item.rating }).map((_, index) => (
                  <Icon key={index} name="star" className="h-4 w-4" />
                ))}
              </div>
              <p className="mt-5 text-sm leading-7 text-slate-700">“{item.quote}”</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-full bg-brand/10 text-sm font-black text-brand">{item.name.slice(0, 1)}</div>
                <div>
                  <p className="text-sm font-black text-ink">{item.name}</p>
                  <p className="text-xs font-bold text-slate-500">{item.location}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section className="px-4 py-14 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <SectionHeading
          title="Preguntas frecuentes"
          subtitle="Información útil para que el usuario sepa qué puede mantener y cómo funciona la página."
        />
        <div className="mt-8 space-y-3">
          {content.faq.map((item) => (
            <details key={item.question} className="rounded-[1.5rem] border border-white/70 bg-white p-5 shadow-sm">
              <summary className="cursor-pointer list-none text-base font-black text-ink">{item.question}</summary>
              <p className="mt-3 text-sm leading-6 text-slate-600">{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Home() {
  return (
    <main>
      <Hero />
      <Benefits />
      <FeaturedDestinations />
      <Process />
      <Testimonials />
      <FAQ />
      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl overflow-hidden rounded-[2.5rem] bg-ink text-white shadow-travel">
          <div className="grid gap-8 p-8 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <h2 className="text-3xl font-black tracking-tight md:text-5xl">¿Tenés una idea de viaje?</h2>
              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-300">
                La convertimos en una cotización clara con destinos, actividades, traslados y opciones para ajustar presupuesto.
              </p>
            </div>
            <Button as={Link} to="/cotizar" variant="primary" className="md:w-auto">
              Empezar ahora
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}

function DestinationsPage() {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const [search, setSearch] = useState(initialSearch);
  const [category, setCategory] = useState('Todos');

  const categories = useMemo(() => ['Todos', ...Array.from(new Set(content.destinations.map((item) => item.category)))], []);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return content.destinations.filter((item) => {
      const matchesCategory = category === 'Todos' || item.category === category;
      const haystack = `${item.title} ${item.destination} ${item.region} ${item.tags.join(' ')} ${item.description}`.toLowerCase();
      return matchesCategory && (!term || haystack.includes(term));
    });
  }, [category, search]);

  return (
    <main className="px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="rounded-[2.5rem] bg-white p-6 shadow-sm md:p-10">
          <p className="text-sm font-black uppercase tracking-[0.18em] text-coral">Catálogo de destinos</p>
          <h1 className="mt-4 max-w-4xl text-4xl font-black tracking-tight text-ink md:text-6xl">Explorá Costa Rica a tu ritmo</h1>
          <p className="mt-5 max-w-3xl text-base leading-7 text-slate-600 md:text-lg">
            Filtrá por tipo de experiencia o buscá por destino. Cada tarjeta lleva a un detalle funcional con itinerario, incluye/no incluye y botón de cotización.
          </p>

          <div className="mt-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              {categories.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setCategory(item)}
                  className={`rounded-full px-4 py-2 text-sm font-black transition ${
                    category === item ? 'bg-brand text-white' : 'bg-slate-100 text-slate-600 hover:bg-brand/10 hover:text-brand'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600">
              <Icon name="map" className="h-4 w-4 text-brand" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar destino..."
                className="min-w-0 flex-1 bg-transparent outline-none"
              />
            </label>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-8 rounded-[2rem] border border-dashed border-slate-300 bg-white p-8 text-center">
            <p className="text-lg font-black text-ink">No encontramos destinos con ese filtro.</p>
            <p className="mt-2 text-sm text-slate-600">Probá con otra búsqueda o seleccioná “Todos”.</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((destination) => (
              <DestinationCard key={destination.slug} destination={destination} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function TourDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const destination = getDestination(slug);

  if (!destination) {
    return <Navigate to="/destinos" replace />;
  }

  const similar = content.destinations.filter((item) => item.slug !== destination.slug).slice(0, 3);

  return (
    <main>
      <section className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
          <TravelImage
            src={destination.image}
            alt={destination.imageAlt}
            className="aspect-[4/3] rounded-[2.5rem] shadow-travel sm:aspect-[16/10]"
          />
          <div className="rounded-[2.5rem] border border-white/70 bg-white p-6 shadow-sm md:p-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-brand/10 px-3 py-1 text-xs font-black text-brand">{destination.category}</span>
              <span className="rounded-full bg-coral-soft px-3 py-1 text-xs font-black text-coral">{destination.duration}</span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-600">{destination.rating} ★</span>
            </div>
            <h1 className="mt-5 text-3xl font-black tracking-tight text-ink md:text-5xl">{destination.title}</h1>
            <p className="mt-4 text-base leading-7 text-slate-600">{destination.description}</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <div className="rounded-3xl bg-slate-50 p-4">
                <Icon name="calendar" className="text-brand" />
                <p className="mt-2 text-xs font-black uppercase tracking-[0.12em] text-slate-400">Duración</p>
                <p className="mt-1 text-sm font-black text-ink">{destination.duration}</p>
              </div>
              <div className="rounded-3xl bg-slate-50 p-4">
                <Icon name="mapPin" className="text-brand" />
                <p className="mt-2 text-xs font-black uppercase tracking-[0.12em] text-slate-400">Región</p>
                <p className="mt-1 text-sm font-black text-ink">{destination.region}</p>
              </div>
              <div className="rounded-3xl bg-slate-50 p-4">
                <Icon name="dollar" className="text-brand" />
                <p className="mt-2 text-xs font-black uppercase tracking-[0.12em] text-slate-400">Desde</p>
                <p className="mt-1 text-sm font-black text-ink">{destination.price}</p>
              </div>
            </div>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Button as={Link} to={`/cotizar?destino=${encodeURIComponent(destination.slug)}`} variant="primary">
                Cotizar este viaje
              </Button>
              <Button
                onClick={() => openWhatsApp(buildWhatsAppMessage({ destino: formatDestinationLabel(destination), mensaje: `Quiero más información sobre ${destination.title}.` }, 'Detalle de tour'))}
                variant="secondary"
              >
                <Icon name="whatsapp" />
                Preguntar por WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-3">
          <div className="rounded-[2rem] border border-white/70 bg-white p-6 shadow-sm lg:col-span-2">
            <h2 className="text-2xl font-black text-ink">Itinerario sugerido</h2>
            <ol className="mt-5 space-y-4">
              {destination.itinerary.map((item, index) => (
                <li key={item} className="flex gap-4">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-2xl bg-brand text-sm font-black text-white">{index + 1}</span>
                  <p className="text-sm leading-6 text-slate-700">{item}</p>
                </li>
              ))}
            </ol>
          </div>
          <div className="space-y-6">
            <div className="rounded-[2rem] border border-white/70 bg-white p-6 shadow-sm">
              <h3 className="text-xl font-black text-ink">Incluye</h3>
              <ul className="mt-4 space-y-3">
                {destination.includes.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-6 text-slate-700">
                    <Icon name="check" className="mt-0.5 h-5 w-5 text-brand" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-[2rem] border border-white/70 bg-white p-6 shadow-sm">
              <h3 className="text-xl font-black text-ink">No incluye</h3>
              <ul className="mt-4 space-y-3">
                {destination.notIncludes.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-6 text-slate-700">
                    <span className="mt-1 h-2 w-2 rounded-full bg-coral" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading title="También podrías considerar" subtitle="Opciones relacionadas para armar un itinerario combinado." />
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {similar.map((item) => (
              <DestinationCard key={item.slug} destination={item} compact />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function QuotePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const destinationSlug = searchParams.get('destino');
  const selectedDestination = getDestination(destinationSlug);
  const [form, setForm] = useState({
    ...initialQuoteForm,
    destino: selectedDestination ? formatDestinationLabel(selectedDestination) : ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (selectedDestination) {
      setForm((value) => ({ ...value, destino: formatDestinationLabel(selectedDestination) }));
    }
  }, [selectedDestination]);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setError('');
    setSuccess(false);
  }

  function submit(event) {
    event.preventDefault();
    if (!form.nombre || !form.telefono || !form.destino) {
      setError('Completá nombre, teléfono y destino para poder enviarte una cotización.');
      return;
    }
    const message = buildWhatsAppMessage(form, 'Cotización TicoMundo Viajes');
    setSuccess(true);
    openWhatsApp(message);
  }

  return (
    <main className="px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-[2.5rem] bg-ink p-8 text-white shadow-travel">
          <p className="text-sm font-black uppercase tracking-[0.18em] text-sky-100">{content.quote.title}</p>
          <h1 className="mt-4 text-4xl font-black tracking-tight md:text-6xl">{content.quote.title}</h1>
          <p className="mt-5 text-base leading-7 text-slate-300">{content.quote.subtitle}</p>
          <div className="mt-8 grid gap-4">
            {[
              ['Itinerario claro', 'Destinos, traslados, hospedaje y actividades.'],
              ['Ajuste de presupuesto', 'Opciones para subir o bajar según lo que necesites.'],
              ['Respuesta rápida', 'Te contactamos por WhatsApp el mismo día.']
            ].map(([title, description]) => (
              <div key={title} className="rounded-[1.5rem] bg-white/10 p-5">
                <h3 className="font-black">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-300">{description}</p>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="rounded-[2.5rem] border border-white/70 bg-white p-6 shadow-sm md:p-8">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-2 text-sm font-black text-slate-700">
              Nombre completo
              <input value={form.nombre} onChange={(event) => update('nombre', event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-brand focus:ring-4 focus:ring-brand/10" placeholder="Tu nombre" />
            </label>
            <label className="grid gap-2 text-sm font-black text-slate-700">
              Teléfono / WhatsApp
              <input value={form.telefono} onChange={(event) => update('telefono', event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-brand focus:ring-4 focus:ring-brand/10" placeholder="+506 0000 0000" />
            </label>
            <label className="grid gap-2 text-sm font-black text-slate-700">
              Email
              <input value={form.email} onChange={(event) => update('email', event.target.value)} type="email" className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-brand focus:ring-4 focus:ring-brand/10" placeholder="correo@ejemplo.com" />
            </label>
            <label className="grid gap-2 text-sm font-black text-slate-700">
              Fecha aproximada
              <input value={form.fecha} onChange={(event) => update('fecha', event.target.value)} type="month" className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-brand focus:ring-4 focus:ring-brand/10" />
            </label>
            <label className="grid gap-2 text-sm font-black text-slate-700">
              Viajeros
              <input value={form.viajeros} onChange={(event) => update('viajeros', event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-brand focus:ring-4 focus:ring-brand/10" placeholder="2 adultos, 1 niño..." />
            </label>
            <label className="grid gap-2 text-sm font-black text-slate-700">
              Presupuesto aproximado
              <input value={form.presupuesto} onChange={(event) => update('presupuesto', event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-brand focus:ring-4 focus:ring-brand/10" placeholder="Ej: $800 - $1200" />
            </label>
            <label className="col-span-1 grid gap-2 text-sm font-black text-slate-700 sm:col-span-2">
              Destino o idea
              <select value={form.destino} onChange={(event) => update('destino', event.target.value)} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-brand focus:ring-4 focus:ring-brand/10">
                <option value="">Seleccioná una opción o escribí una idea</option>
                {content.destinations.map((destination) => (
                  <option key={destination.slug} value={formatDestinationLabel(destination)}>
                    {destination.destination} — {destination.title}
                  </option>
                ))}
                <option value="Viaje personalizado">Viaje personalizado</option>
              </select>
            </label>
            <label className="col-span-1 grid gap-2 text-sm font-black text-slate-700 sm:col-span-2">
              Contanos más
              <textarea value={form.mensaje} onChange={(event) => update('mensaje', event.target.value)} rows={5} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-brand focus:ring-4 focus:ring-brand/10" placeholder="Actividades, hoteles, ritmo del viaje, necesidades especiales..." />
            </label>
          </div>

          {error && <p className="mt-4 rounded-2xl bg-coral-soft p-4 text-sm font-bold text-coral">{error}</p>}
          {success && <p className="mt-4 rounded-2xl bg-brand/10 p-4 text-sm font-bold text-brand">{content.quote.successMessage}</p>}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button type="submit" variant="primary" className="w-full sm:w-auto">
              <Icon name="whatsapp" />
              {content.quote.submitButton}
            </Button>
            <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={() => { setForm(initialQuoteForm); setError(''); setSuccess(false); navigate('/destinos'); }}>
              Volver a destinos
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}

function ContactPage() {
  const [form, setForm] = useState({ nombre: '', mensaje: '' });
  const [success, setSuccess] = useState(false);

  function submit(event) {
    event.preventDefault();
    const message = buildWhatsAppMessage({ ...form, destino: 'Consulta general', mensaje: form.mensaje || 'Consulta general desde la página.' }, 'Contacto TicoMundo Viajes');
    setSuccess(true);
    openWhatsApp(message);
  }

  return (
    <main className="px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1fr_0.9fr]">
        <div className="rounded-[2.5rem] border border-white/70 bg-white p-8 shadow-sm">
          <p className="text-sm font-black uppercase tracking-[0.18em] text-coral">{content.contact.title}</p>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-ink md:text-6xl">{content.contact.title}</h1>
          <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600">{content.contact.subtitle}</p>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <a href={`https://wa.me/${content.contact.whatsapp}`} className="rounded-[1.5rem] bg-brand/10 p-5 text-brand transition hover:bg-brand hover:text-white">
              <Icon name="whatsapp" className="h-7 w-7" />
              <p className="mt-3 text-sm font-black">WhatsApp</p>
              <p className="mt-1 text-xs font-bold text-slate-500 group-hover:text-white/80">{content.contact.phone}</p>
            </a>
            <a href={`mailto:${content.contact.email}`} className="rounded-[1.5rem] bg-sky-100 p-5 text-sky-900 transition hover:bg-sky-500 hover:text-white">
              <Icon name="mail" className="h-7 w-7" />
              <p className="mt-3 text-sm font-black">Email</p>
              <p className="mt-1 text-xs font-bold text-slate-500 group-hover:text-white/80">{content.contact.email}</p>
            </a>
            <div className="rounded-[1.5rem] bg-coral-soft p-5 text-coral">
              <Icon name="mapPin" className="h-7 w-7" />
              <p className="mt-3 text-sm font-black">Ubicación</p>
              <p className="mt-1 text-xs font-bold text-slate-500">{content.contact.location}</p>
            </div>
          </div>

          <div className="mt-8 rounded-[2rem] bg-slate-50 p-6">
            <h2 className="text-xl font-black text-ink">Horario de atención</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">{content.contact.hours}</p>
            <p className="mt-2 text-sm font-bold text-brand">{content.contact.responseTime}</p>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-[2.5rem] border border-white/70 bg-white p-8 shadow-sm">
          <h2 className="text-2xl font-black text-ink">{content.contact.formTitle}</h2>
          <div className="mt-6 grid gap-4">
            <label className="grid gap-2 text-sm font-black text-slate-700">
              Nombre
              <input value={form.nombre} onChange={(event) => setForm({ ...form, nombre: event.target.value })} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-brand focus:ring-4 focus:ring-brand/10" placeholder="Tu nombre" />
            </label>
            <label className="grid gap-2 text-sm font-black text-slate-700">
              Mensaje
              <textarea value={form.mensaje} onChange={(event) => setForm({ ...form, mensaje: event.target.value })} rows={6} className="rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-brand focus:ring-4 focus:ring-brand/10" placeholder="Escribinos qué necesitás..." />
            </label>
          </div>
          {success && <p className="mt-4 rounded-2xl bg-brand/10 p-4 text-sm font-bold text-brand">Abrimos WhatsApp con tu mensaje. Revisalo y enviá cuando quieras.</p>}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button type="submit" variant="primary" className="w-full sm:w-auto">
              <Icon name="whatsapp" />
              {content.contact.submitButton}
            </Button>
            <a href={`mailto:${content.contact.email}`} className="inline-flex items-center justify-center rounded-full border border-brand/20 bg-white px-5 py-3 text-sm font-extrabold text-brand transition hover:bg-brand/10">
              Enviar correo
            </a>
          </div>
        </form>
      </div>
    </main>
  );
}

function NotFound() {
  return (
    <main className="grid min-h-[70dvh] place-items-center px-4 text-center">
      <div>
        <p className="text-sm font-black uppercase tracking-[0.18em] text-coral">Página no encontrada</p>
        <h1 className="mt-4 text-4xl font-black tracking-tight text-ink md:text-6xl">Ups, esa ruta no existe.</h1>
        <p className="mt-5 text-slate-600">Volvé al inicio y seguimos armando tu viaje.</p>
        <div className="mt-8">
          <Button as={Link} to="/" variant="primary">Volver al inicio</Button>
        </div>
      </div>
    </main>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/70 bg-white px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-[1fr_auto] md:items-center">
        <div>
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand text-lg font-black text-white">{content.brand.logoMark}</span>
            <div>
              <p className="text-base font-black text-ink">{content.brand.name}</p>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-coral">{content.brand.tagline}</p>
            </div>
          </div>
          <p className="mt-4 max-w-xl text-sm leading-6 text-slate-600">
            POC funcional basada en las pantallas de Stitch. El contenido visible se mantiene desde <code>src/data/site-content.json</code>.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {content.nav.map((item) => (
            <Link key={item.to} to={item.to} className="rounded-full bg-slate-100 px-4 py-2 text-sm font-black text-slate-700 transition hover:bg-brand hover:text-white">
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error('Error en la página:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="grid min-h-screen place-items-center bg-sand px-4 text-center">
          <div className="rounded-[2rem] bg-white p-8 shadow-travel">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-coral">Algo salió mal</p>
            <h1 className="mt-3 text-3xl font-black text-ink">La página no pudo cargar correctamente.</h1>
            <p className="mt-4 text-sm leading-6 text-slate-600">Recargá la página o intentá de nuevo en unos minutos.</p>
            <button type="button" onClick={() => window.location.reload()} className="mt-6 rounded-full bg-brand px-5 py-3 text-sm font-black text-white">
              Recargar
            </button>
          </div>
        </main>
      );
    }

    return this.props.children;
  }
}

function App() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  useEffect(() => {
    const titles = {
      '/': `${content.brand.name} | ${content.brand.tagline}`,
      '/destinos': `Destinos | ${content.brand.name}`,
      '/cotizar': `Cotizar viaje | ${content.brand.name}`,
      '/contacto': `Contacto | ${content.brand.name}`
    };
    document.title = titles[location.pathname] || `${content.brand.name}`;
  }, [location.pathname]);

  return (
    <ErrorBoundary>
      <div className="min-h-screen">
        <Header />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/destinos" element={<DestinationsPage />} />
          <Route path="/destinos/:slug" element={<TourDetailPage />} />
          <Route path="/cotizar" element={<QuotePage />} />
          <Route path="/contacto" element={<ContactPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Footer />
      </div>
    </ErrorBoundary>
  );
}

export default App;
